"use client";

import { useEffect, useRef, useState } from "react";

import Section from "@/components/common/Section/Section";
import Tag from "@/components/common/Tag/Tag";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { applyImgParamsToUrl, formatPrice } from "@/utils/general";

import { addQuizResurfaceToCart } from "./quizResurfaceAtc";
import styles from "./QuizResurfaceBanner.module.scss";
import { QuizResurfaceProduct } from "./quizResurfaceProduct";

type QuizResurfaceBannerPanelProps = {
  product: QuizResurfaceProduct;
  displayPrice: number | undefined;
  savingsLabel?: string | null;
  promoLive: boolean;
  onClose: () => void;
};

const QuizResurfaceBannerPanel = ({
  product,
  displayPrice,
  savingsLabel,
  promoLive,
  onClose,
}: QuizResurfaceBannerPanelProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showTooltip) return undefined;
    const handleHide = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowTooltip(false);
      }
    };
    const id = window.setTimeout(() => document.addEventListener("click", handleHide), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", handleHide);
    };
  }, [showTooltip]);

  const imageSrc = product.imageUrl ? applyImgParamsToUrl(product.imageUrl, { w: "350" }) : "";

  return (
    <div className={`max-w-[1570px] mx-auto lg:px-[25px] ${styles.floatWrap}`}>
      <Section
        px="px-4 md:px-20"
        py="py-4 md:py-12 lg:py-20"
        className={`relative ${styles.panel}`}
      >
        <div className="mb-4 md:mb-0 md:absolute md:top-8 md:right-8 lg:top-16 lg:right-[171px]">
          <ButtonNova label="Close" level="Textlink" className="ml-auto" onClick={onClose} />
        </div>
        <div className="pb-4 mb-4 md:pb-8 md:mb-8 border-b md:px-8 border-nova-transparency-black-20">
          <TextElementNova
            text="Your Personalized Plan"
            className="Title-20-Super-Normal text-nova-brand-primary-100 mb-2"
          />
          <div className="flex gap-1 items-center">
            <TextElementNova
              text="Hair growth tailored to your body’s needs."
              className="Body-30-Dia-Normal"
            />
            <div ref={tooltipRef} className="relative h-4 w-4">
              <button
                type="button"
                aria-label="More information"
                onClick={() => setShowTooltip(true)}
              >
                <span className="block h-4 w-4 rounded-full border border-[#5a6969] text-[10px] leading-4 text-center text-[#5a6969]">
                  i
                </span>
              </button>
              {showTooltip ? (
                <div className={styles.tooltip}>
                  <TextElementNova
                    text="Results are based on your responses to the quiz."
                    className="Body-10-Dia-Normal"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row xl:px-8 gap-4">
          <div className="flex-1 flex gap-4 flex-wrap">
            <div className="w-[120px] md:w-[104px] xl:w-[175px]">
              {imageSrc ? (
                <Picture src={imageSrc} alt="" className="w-full h-auto object-contain" />
              ) : null}
            </div>
            <div className="flex-1 justify-center flex flex-col">
              <TextElementNova text={product.name || ""} className="Body-20-Dia-Strong mb-2" />
              <div className="flex gap-2 items-center flex-wrap mb-2">
                <TextElementNova
                  text={`$${formatPrice(Number(product.price))}`}
                  className="Body-10-Dia-Normal text-nova-transparency-black-60 line-through"
                />
                <TextElementNova
                  text={`$${formatPrice(Number(displayPrice))}/mo`}
                  className="Body-20-Dia-Strong"
                />
                {savingsLabel ? (
                  <Tag data={{ line: savingsLabel, classes: "bg-nova-brand-accent-80" }} />
                ) : null}
              </div>
              <TextElementNova
                text="A 30-day supply delivered each month."
                className="Caption-10-Dia-Normal"
              />
              <div className="w-full hidden lg:block mt-4">
                <TextElementNova
                  text={product.description || ""}
                  className="Body-20-Dia-Normal max-w-[343px]"
                />
              </div>
            </div>
            <div className="w-full block lg:hidden">
              <TextElementNova
                text={product.description || ""}
                className="Body-20-Dia-Normal max-w-[343px]"
              />
            </div>
          </div>
          <div className="w-full md:w-[240px] lg:w-[370px] xl:w-[483px]">
            <ButtonNova
              label="Subscribe"
              icon="Right"
              width="100%"
              className="mb-2 !p-3"
              onClick={() => addQuizResurfaceToCart(product, displayPrice, promoLive)}
            />
            <TextElementNova
              text="Free shipping. Pause or cancel anytime."
              className="Caption-10-Dia-Normal text-nova-transparency-black-60 text-center mb-5"
            />
            <ButtonNova
              label={`Buy Once for $${formatPrice(Number(product.price))} + $6.95 shipping`}
              level="Textlink"
              className="mx-auto mb-2"
              onClick={() =>
                addQuizResurfaceToCart(product, displayPrice, promoLive, `${product.sku}-N01`)
              }
            />
            <TextElementNova
              text="We recommend 3-6 months of <span class='whitespace-nowrap font-dinamoRegular'>consistent use to see results.</span>"
              className="Body-10-Dia-Normal text-nova-brand-primary-80 text-center"
            />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default QuizResurfaceBannerPanel;
